import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores'
import { User } from '@/types'
import { logger } from '@/utils/logger'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRoles?: string[]
  requiredPermissions?: (user: User) => boolean
}

/**
 * Component to protect routes based on user authentication and roles.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRoles, 
  requiredPermissions 
}) => {
  const user = useAuthStore(state => state.user)
  const location = useLocation()

  if (!user) {
    // Redirect to login if not authenticated
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check if user has at least one of the required roles
  const hasRole = requiredRoles 
    ? requiredRoles.some(role => {
        if (role === 'admin') return user.role === 'admin' || user.is_admin
        if (role === 'moderator') return user.role === 'moderator' || user.is_moderator || user.role === 'admin' || user.is_admin
        if (role === 'grafik') return user.role === 'grafik' || user.is_grafik || user.is_admin || user.is_system
        if (role === 'kadrowa') return user.role === 'kadrowa' || user.is_kadrowa || user.is_admin || user.is_system
        if (role === 'ksiegowa') return user.role === 'ksiegowa' || user.is_ksiegowa || user.is_admin
        return user.role === role
      })
    : true
    
  const hasPermission = requiredPermissions ? requiredPermissions(user) : true

  if (!hasRole || !hasPermission) {
    logger.captureMessage('Unauthorized access attempt blocked by ProtectedRoute', 'warning', { 
        url: location.pathname,
        userRole: user.role, 
        email: user.email,
        requiredRoles
    })
    return <Navigate to="/access-denied" replace />
  }

  return <>{children}</>
}
